/**
 * «Морской бой» — страница /battleship: две доски, клик по клетке, три соперника.
 *
 * Правила и ход соперника — public/js/battleship/engine.js: тот же алгоритм,
 * что играет голосом на колонке и в голосовом режиме (PHP); паритет держит
 * BattleshipEngineParityTest. Здесь — только доска, расстановка, звук и слова.
 *
 * Доступность — public/js/a11y-grid.js: каждая клетка — элемент с названием и
 * состоянием («Б5, мимо»), стрелки двигают по полю, Enter стреляет; ход и
 * выстрелы соперника объявляются живой областью примитива. Выстрелы соперника
 * проигрываются по одному (AI_STEP_MS), чтобы каждый было видно и слышно.
 *
 * Расстановка своими руками — до первого выстрела, на копии флота: тап по
 * пустой клетке ставит выбранный корабль носом в неё (у края — сдвигает внутрь),
 * тап по кораблю выбирает его, второй тап — поворачивает; R — поворот с
 * клавиатуры. Генератор партии она не трогает: ходы соперника те же, что у
 * движка с тем же зерном, — паритет с PHP не задет.
 *
 * Звук — те же файлы, что у голосового режима (scripts/battleship-sounds.py),
 * через Web Audio: контекст будится нажатием через __liveAudioCtxReady.
 * ⚠️ Звук игре не обязателен: браузер без настоящего Web Audio получает
 * заглушку из timbrica-core.js, которая на каждом `new AudioContext()` кидает
 * тост «нужен Web Audio API». Игра проверяет конструктор ДО вызова и тогда
 * просто молчит — иначе тост вылезал на каждом тапе по клетке.
 *
 * Незаконченная партия и счёт по соперникам — __toolSettings: перезагрузка
 * страницы партию не теряет. Тексты — только из перевода (data-i18n).
 *
 * Телеметрия (имена — в полезной нагрузке: сервер принимает только известные
 * типы событий, см. разбор в checkers.blade.php):
 *   feature_use  {f: bs_*}            — что делал игрок (соперник, расстановка,
 *                                        новая партия посреди старой, звук…);
 *   tool_complete {t: 'bs_game_end'}   — партия кончилась: исход, соперник,
 *                                        выстрелы, точность, время, чем играли.
 */
(function (global, doc) {
    'use strict';

    var SLUG = 'battleship';
    var AI_STEP_MS = 650;
    var SIDES = ['enemy', 'mine'];
    /* Пауза между выстрелами длиннее этой — человек отошёл; в «время партии» не идёт. */
    var IDLE_CAP_MS = 120000;

    function parse(s, def) {
        try { return JSON.parse(s || ''); } catch (e) { return def; }
    }

    function seed() {
        try {
            var a = new Uint32Array(1);
            (global.crypto || global.msCrypto).getRandomValues(a);
            return a[0];
        } catch (e) {
            return Math.floor(Math.random() * 4294967296);
        }
    }

    function now() {
        try { if (typeof global.__activeNow === 'function') return global.__activeNow(); } catch (e) {}
        return Date.now();
    }

    function boot() {
        var root = doc.querySelector('[data-bs]');
        var E = global.BattleshipEngine;
        if (!root || !E || root.__bs) return;
        root.__bs = true;

        var T = parse(root.getAttribute('data-i18n'), {});
        var SND = parse(root.getAttribute('data-sounds'), {});
        var COLS = Array.from ? Array.from(T.cols || '') : String(T.cols || '').split('');
        var A = global.__a11yGrid || null;
        var store = global.__toolSettings || null;

        function q(sel) { return root.querySelector(sel); }
        var statusEl = q('[data-bs-status]');
        var actsEl = q('[data-bs-acts]');
        var shotsEl = q('[data-bs-shots]');
        var scoreEl = q('[data-bs-score]');
        var bestEl = q('[data-bs-best]');
        var logEl = q('[data-bs-log]');
        var newBtn = q('[data-bs-new]');
        var placeBtn = q('[data-bs-place-open]');
        var soundBtn = q('[data-bs-sound]');
        var noteEl = q('[data-bs-seg-note]');
        var placeBox = q('[data-bs-place]');
        var trayEl = q('[data-bs-tray]');
        var doneBtn = q('[data-bs-place-done]');
        var enemySide = q('[data-bs-side="enemy"]');
        var levelInputs = Array.prototype.slice.call(root.querySelectorAll('input[name="bs-level"]'));
        var grids = {}, fleets = {}, afloatEls = {}, cells = {}, ag = {};

        var st = {
            g: null,
            show0: null,   /* своя доска «как показана» — пока проигрываются выстрелы соперника */
            busy: false,
            token: 0,
            log: [],
            last: { enemy: -1, mine: -1 },
            revealed: false,
            place: null,   /* расстановка: {ships: [клетки | null] по FLEET, sel, dir, ghost} */
            meta: null,    /* партия для телеметрии */
            statusText: '',
            statusKind: ''
        };

        function load(key, def) {
            try { var v = store ? store.load(SLUG, key, def) : def; return v === undefined ? def : v; } catch (e) { return def; }
        }
        function save(key, val) {
            try { if (store) store.save(SLUG, key, val); } catch (e) {}
        }

        var soundOn = load('sound', true) !== false;
        var stats = load('stats', {}) || {};

        /* ─── Телеметрия ──────────────────────────────────────── */

        function track(f, d) {
            try {
                if (typeof global.__aeFeature === 'function') global.__aeFeature(f, d || {});
                else if (typeof global.__ae === 'function') global.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, d || {}));
            } catch (e) {}
        }

        function freshMeta() {
            return { ms: 0, at: now(), restored: 0, reshuffles: 0, manual: 0, hits: 0, known: 0, busy: 0, input: {} };
        }

        /* Время партии — сумма пауз между действиями, каждая не длиннее IDLE_CAP_MS. */
        function tick() {
            var m = st.meta;
            if (!m) return;
            var t = now();
            m.ms += Math.max(0, Math.min(IDLE_CAP_MS, t - (m.at || t)));
            m.at = t;
        }

        function durS() { tick(); return st.meta ? Math.round(st.meta.ms / 1000) : 0; }

        function mainInput() {
            var inp = (st.meta && st.meta.input) || {}, best = '', n = 0;
            Object.keys(inp).forEach(function (k) { if (inp[k] > n) { n = inp[k]; best = k; } });
            return best;
        }

        /* ─── Слова ───────────────────────────────────────────── */

        function name(i) { return COLS[i % 10] + (Math.floor(i / 10) + 1); }
        function levelName(lv) { return T['lvl_' + lv] || lv; }
        function shipName(len) { return T['ship_' + len] || String(len); }
        function fill(s, map) {
            return String(s || '').replace(/:(\w+)/g, function (m, k) { return map.hasOwnProperty(k) ? map[k] : m; });
        }
        function trimDot(s) { return String(s).replace(/[.\s]+$/, ''); }
        function shotsAt(b) {
            var n = 0;
            for (var i = 0; i < 100; i++) { var c = b.m.charAt(i); if (c === 'o' || c === 'x' || c === 'X') n++; }
            return n;
        }
        function hitsAt(b) {
            var n = 0;
            for (var i = 0; i < 100; i++) { var c = b.m.charAt(i); if (c === 'x' || c === 'X') n++; }
            return n;
        }
        function myLine(i, r) {
            var w = r.r === 'miss' ? T.r_miss : r.r === 'hit' ? T.r_hit : T.r_sunk + ' (' + shipName(r.len) + ')';
            return name(i) + ' — ' + w;
        }
        function aiLine(e, board) {
            if (e.r === 'miss') return name(e.cell) + ' — ' + T.r_miss;
            return name(e.cell) + ' — ' + fill(e.r === 'hit' ? T.ai_hit : T.ai_sunk, { ship: shipName(board.s[e.ship].length) });
        }
        function mine() { return st.show0 || st.g.b[0]; }
        function cellsText(list) {
            var s = list.slice().sort(function (a, b) { return a - b; });
            return s.length > 1 ? name(s[0]) + '–' + name(s[s.length - 1]) : name(s[0]);
        }

        function label(side, i) {
            var g = st.g;
            if (!g) return name(i);
            var b, m, s;
            if (side === 'enemy') {
                b = g.b[1];
                m = b.m.charAt(i);
                s = m === 'o' ? T.c_miss : m === '+' ? T.c_empty : m === 'x' ? T.c_hit : m === 'X' ? T.c_sunk
                    : (st.revealed && E.shipAt(b, i) >= 0 ? T.c_reveal : T.c_unknown);
            } else if (st.place) {
                var k = placeShipAt(i);
                s = k < 0 ? T.c_water : shipName(E.FLEET[k]) + (k === st.place.sel ? ', ' + T.c_selected : '');
            } else {
                b = mine();
                m = b.m.charAt(i);
                s = E.shipAt(b, i) >= 0
                    ? (m === 'X' ? T.c_ship_sunk : m === 'x' ? T.c_ship_hit : T.c_ship)
                    : (m === 'o' ? T.c_their_miss : m === '+' ? T.c_empty : T.c_water);
            }
            return name(i) + ', ' + s;
        }

        /**
         * Строка статуса. acts — действия прямо в ней («Сыграть ещё», «Начать новую»):
         * ответ на вопрос стоит там же, где вопрос, а не в шапке над досками.
         */
        function status(text, kind, acts) {
            st.statusText = text;
            st.statusKind = kind || '';
            statusEl.textContent = text;
            statusEl.parentNode.className = 'bs-status' + (kind ? ' bs-status--' + kind : '');
            actsEl.textContent = '';
            (acts || []).forEach(function (a) {
                var b = doc.createElement('button');
                b.type = 'button';
                b.className = 'bs-btn' + (a.main ? ' bs-btn--main' : '');
                b.textContent = a.label;
                b.addEventListener('click', function () { wakeAudio(); a.fn(); });
                actsEl.appendChild(b);
            });
            actsEl.hidden = !(acts && acts.length);
        }
        function announce(text, assertive) {
            if (A && A.announce) A.announce(text, !!assertive);
            else if (global.__announce) global.__announce(text);
        }
        function say(text, kind, acts, assertive) {
            status(text, kind, acts);
            announce(text, assertive);
        }

        /* ─── Доски ───────────────────────────────────────────── */

        SIDES.forEach(function (side) {
            grids[side] = q('[data-bs-grid="' + side + '"]');
            fleets[side] = q('[data-bs-fleet="' + side + '"]');
            afloatEls[side] = q('[data-bs-afloat="' + side + '"]');
            cells[side] = [];
            var frag = doc.createDocumentFragment();
            for (var i = 0; i < 100; i++) {
                var c = doc.createElement('div');
                c.className = 'bs-cell';
                c.setAttribute('data-i', String(i));
                cells[side].push(c);
                frag.appendChild(c);
            }
            grids[side].appendChild(frag);
        });

        if (A) {
            ag.enemy = A.create(grids.enemy, {
                name: T.enemy_grid,
                cols: 10,
                cells: function () { return cells.enemy; },
                label: function (el, i) { return label('enemy', i); },
                onActivate: function (i) { fireAt(i, 'key'); }
            });
            ag.mine = A.create(grids.mine, {
                name: T.my_grid,
                cols: 10,
                cells: function () { return cells.mine; },
                label: function (el, i) { return label('mine', i); },
                onActivate: function (i) {
                    if (st.place) placeTap(i, 'key');
                    else announce(label('mine', i));
                }
            });
        }

        /* Чем стреляли: палец, мышь, клавиатура — или клик без нажатия перед ним
           (так щёлкает экранный диктор: VoiceOver, TalkBack, NVDA). */
        var lastPtr = { t: 0, type: '' };
        function ptrDown(e) { lastPtr = { t: Date.now(), type: e.pointerType || 'mouse' }; }
        function viaClick() { return Date.now() - lastPtr.t < 1500 ? lastPtr.type : 'at'; }

        grids.enemy.addEventListener('pointerdown', ptrDown, { passive: true });
        grids.mine.addEventListener('pointerdown', ptrDown, { passive: true });
        grids.enemy.addEventListener('click', function (e) {
            var c = e.target && e.target.closest ? e.target.closest('.bs-cell') : null;
            if (c) fireAt(parseInt(c.getAttribute('data-i'), 10), viaClick());
        });
        grids.mine.addEventListener('click', function (e) {
            if (!st.place) return;
            var c = e.target && e.target.closest ? e.target.closest('.bs-cell') : null;
            if (c) placeTap(parseInt(c.getAttribute('data-i'), 10), viaClick());
        });

        /* Корпус корабля одним целым: соседние клетки того же корабля сшиваются. */
        function joins(ship, i) {
            var s = '';
            var c = i % 10;
            if (c > 0 && ship.indexOf(i - 1) >= 0) s += ' j-l';
            if (c < 9 && ship.indexOf(i + 1) >= 0) s += ' j-r';
            if (ship.indexOf(i - 10) >= 0) s += ' j-t';
            if (ship.indexOf(i + 10) >= 0) s += ' j-b';
            return s;
        }

        function paint(side) {
            if (side === 'mine' && st.place) { paintPlace(); return; }
            var g = st.g;
            var b = side === 'enemy' ? g.b[1] : mine();
            var list = cells[side];
            for (var i = 0; i < 100; i++) {
                var m = b.m.charAt(i);
                var si = E.shipAt(b, i);
                var cls = 'bs-cell';
                if (side === 'mine' && si >= 0) cls += ' bs-c--ship' + joins(b.s[si], i);
                if (side === 'enemy' && st.revealed && si >= 0 && m === '.') cls += ' bs-c--reveal';
                if (m === 'o') cls += ' bs-c--miss';
                else if (m === '+') cls += ' bs-c--empty';
                else if (m === 'x') cls += ' bs-c--hit';
                else if (m === 'X') cls += ' bs-c--sunk';
                if (st.last[side] === i) cls += ' bs-c--last';
                if (list[i].className !== cls) list[i].className = cls;
                if (list[i].getAttribute('data-s') !== m) list[i].setAttribute('data-s', m);
            }
            if (ag[side]) ag[side].relabel();
            paintFleet(side, b);
        }

        function paintFleet(side, b) {
            var box = fleets[side];
            box.textContent = '';
            var order = b.s.map(function (s, si) { return si; }).sort(function (x, y) { return b.s[y].length - b.s[x].length || x - y; });
            order.forEach(function (si) {
                var span = doc.createElement('span');
                span.className = 'bs-ship' + (E.sunk(b, si) ? ' bs-ship--sunk' : '');
                for (var k = 0; k < b.s[si].length; k++) span.appendChild(doc.createElement('i'));
                box.appendChild(span);
            });
            afloatEls[side].textContent = fill(T.afloat, { n: E.afloat(b).length });
        }

        function paintAll() {
            paint('enemy');
            paint('mine');
            paintCounts();
            paintLog();
            paintLevel();
            updateControls();
        }

        function paintCounts() {
            var g = st.g;
            shotsEl.textContent = fill(T.shots, { n: shotsAt(g.b[1]) });
            /* Счёт — с соперником ЭТОЙ партии, а не с тем, кто отмечен на переключателе
               для следующей: иначе победа над капитаном читалась как «Адмирал: 0 — 0». */
            var lv = g.lv;
            var rec = stats[lv] || [0, 0];
            scoreEl.textContent = fill(T.score, { name: levelName(lv), w: rec[0], l: rec[1] });
            bestEl.textContent = rec[2] ? fill(T.best, { n: rec[2] }) : '';
            bestEl.hidden = !rec[2];
        }

        function paintLog() {
            logEl.textContent = '';
            st.log.slice(0, 12).forEach(function (e) {
                var li = doc.createElement('li');
                var who = doc.createElement('b');
                who.textContent = e.w + ': ';
                li.appendChild(who);
                li.appendChild(doc.createTextNode(e.t));
                logEl.appendChild(li);
            });
        }

        function addLog(who, text) {
            st.log.unshift({ w: who, t: text });
            if (st.log.length > 40) st.log.length = 40;
            paintLog();
        }

        function fx(side, i, kind) {
            var el = cells[side][i];
            if (!el) return;
            var s = doc.createElement('span');
            s.className = 'bs-fx bs-fx--' + kind;
            el.appendChild(s);
            setTimeout(function () { if (s.parentNode) s.parentNode.removeChild(s); }, 1100);
        }

        /* ─── Управление ──────────────────────────────────────── */

        function currentLevel() {
            for (var k = 0; k < levelInputs.length; k++) if (levelInputs[k].checked) return levelInputs[k].value;
            return st.g ? st.g.lv : 'captain';
        }

        function paintLevel() {
            var lv = currentLevel();
            levelInputs.forEach(function (inp) {
                var lab = inp.closest ? inp.closest('label') : inp.parentNode;
                if (lab) lab.classList.toggle('is-on', inp.checked);
            });
            if (noteEl) noteEl.textContent = levelName(lv) + ' — ' + (T['lvl_' + lv + '_d'] || '');
        }

        function setLevelRadio(lv) {
            levelInputs.forEach(function (inp) { inp.checked = inp.value === lv; });
            paintLevel();
        }

        function inProgress() { return !!(st.g && st.g.win < 0 && !E.fresh(st.g)); }

        function updateControls() {
            var g = st.g;
            var canPlace = !!(g && g.win < 0 && E.fresh(g) && !st.busy);
            placeBtn.hidden = !canPlace || !!st.place;
            soundBtn.setAttribute('aria-pressed', soundOn ? 'true' : 'false');
            soundBtn.classList.toggle('is-off', !soundOn);
            root.classList.toggle('bs-placing', !!st.place);
            if (enemySide) enemySide.hidden = !!st.place;
            placeBox.hidden = !st.place;
        }

        function persist() {
            if (!st.g || st.g.win >= 0) { save('game', null); return; }
            tick();
            save('game', { g: st.g, log: st.log.slice(0, 40), meta: st.meta });
        }

        function startStatus() {
            status(T.st_start);
        }

        function newGame(lv) {
            st.token++;
            st.busy = false;
            root.classList.remove('bs-busy');
            st.g = E.newGame(seed(), lv || currentLevel());
            st.show0 = null;
            st.revealed = false;
            st.place = null;
            st.over = null;
            st.log = [];
            st.last = { enemy: -1, mine: -1 };
            st.meta = freshMeta();
            setLevelRadio(st.g.lv);
            paintAll();
            startStatus();
            persist();
            try { if (global.__stage) global.__stage('ready', { level: st.g.lv }); } catch (e) {}
        }

        /* Бросить начатую партию — только по ответу «да»: случайный тап по «Новой игре»
           не должен стирать полчаса боя. */
        function abandonAnd(fn) {
            if (inProgress()) {
                track('bs_abandon', { level: st.g.lv, shots: shotsAt(st.g.b[1]), afloat: E.afloat(st.g.b[0]).length, dur_s: durS() });
            }
            fn();
        }

        function restoreStatus() {
            if (!st.g) return;
            if (st.g.win >= 0 && st.over) { status(st.over.text, st.over.kind, playAgain()); return; }
            status(E.fresh(st.g) ? T.st_start : T.st_turn);
        }

        function playAgain() {
            var after = st.g && st.g.win === 0 ? 'win' : 'loss';
            return [{ label: T.play_again, main: true, fn: function () { track('bs_new', { level: currentLevel(), after: after }); newGame(currentLevel()); announce(T.st_start); } }];
        }

        function valid(g) {
            return g && g.b && g.b.length === 2 && E.LEVELS.indexOf(g.lv) >= 0 && g.win === -1
                && typeof g.rng === 'number' && g.b[0].m && g.b[0].m.length === 100 && g.b[1].m && g.b[1].m.length === 100
                && g.b[0].s && g.b[0].s.length === E.FLEET.length && g.b[1].s && g.b[1].s.length === E.FLEET.length;
        }

        newBtn.addEventListener('click', function () {
            wakeAudio();
            if (st.place) cancelPlace();
            if (inProgress()) {
                say(T.confirm_new, '', [
                    { label: T.new_yes, main: true, fn: function () { abandonAnd(function () { newGame(currentLevel()); announce(T.st_start); }); } },
                    { label: T.keep, fn: function () { restoreStatus(); announce(st.statusText); } }
                ]);
                return;
            }
            track('bs_new', { level: currentLevel(), after: st.g && st.g.win >= 0 ? (st.g.win === 0 ? 'win' : 'loss') : 'fresh' });
            newGame(currentLevel());
            announce(T.st_start);
        });

        soundBtn.addEventListener('click', function () {
            soundOn = !soundOn;
            save('sound', soundOn);
            updateControls();
            track('bs_sound', { on: soundOn ? 1 : 0 });
            if (soundOn) wakeAudio();
        });

        levelInputs.forEach(function (inp) {
            inp.addEventListener('change', function () {
                var lv = inp.value;
                paintLevel();
                save('level', lv);
                track('bs_level', { level: lv, mid: inProgress() ? 1 : 0 });
                if (!st.g || st.g.win >= 0) {
                    newGame(lv);
                } else if (E.fresh(st.g) && !st.busy) {
                    st.g.lv = lv;
                    paintCounts();
                    if (!st.place) startStatus();
                    persist();
                } else {
                    say(fill(T.level_q, { name: levelName(lv) }), '', [
                        { label: fill(T.level_yes, { name: levelName(lv) }), main: true, fn: function () { abandonAnd(function () { newGame(lv); announce(T.st_start); }); } },
                        { label: T.keep, fn: function () { setLevelRadio(st.g.lv); save('level', st.g.lv); restoreStatus(); announce(st.statusText); } }
                    ]);
                }
            });
        });

        /* ─── Расстановка своими руками ───────────────────────── */

        function placeShipAt(i) {
            var p = st.place;
            for (var k = 0; k < p.ships.length; k++) if (p.ships[k] && p.ships[k].indexOf(i) >= 0) return k;
            return -1;
        }

        function dirOf(ship) {
            return ship && ship.length > 1 && ship[1] - ship[0] === 10 ? 1 : 0;
        }

        /* Клетки корабля носом в i; у края — сдвинуть внутрь, а не отказать. */
        function cellsFrom(i, len, dir) {
            var r = Math.floor(i / 10), c = i % 10, out = [];
            if (dir === 0) c = Math.min(c, 10 - len);
            else r = Math.min(r, 10 - len);
            for (var k = 0; k < len; k++) out.push(dir === 0 ? r * 10 + c + k : (r + k) * 10 + c);
            return out;
        }

        /* Встанет ли: ни одной клетки вплотную к другим кораблям, даже углом. */
        function fits(k, list) {
            var ships = st.place.ships;
            for (var j = 0; j < ships.length; j++) {
                if (j === k || !ships[j]) continue;
                for (var a = 0; a < list.length; a++) {
                    for (var b = 0; b < ships[j].length; b++) {
                        var x = list[a], y = ships[j][b];
                        if (Math.abs(Math.floor(x / 10) - Math.floor(y / 10)) <= 1 && Math.abs(x % 10 - y % 10) <= 1) return false;
                    }
                }
            }
            return true;
        }

        function nextUnplaced(from) {
            var ships = st.place.ships;
            for (var n = 0; n < ships.length; n++) {
                var k = (from + n) % ships.length;
                if (!ships[k]) return k;
            }
            return -1;
        }

        function placedCount() {
            return st.place.ships.filter(function (s) { return !!s; }).length;
        }

        function openPlace() {
            if (!st.g || st.g.win >= 0 || !E.fresh(st.g) || st.busy) return;
            wakeAudio();
            st.place = { ships: st.g.b[0].s.map(function (s) { return s.slice(); }), sel: -1, dir: 0, ghost: null };
            st.meta.placeOpen = (st.meta.placeOpen || 0) + 1;
            track('bs_place_open', {});
            updateControls();
            paintPlace();
            say(T.st_place);
            if (ag.mine) ag.mine.focus(0, true);
        }

        function cancelPlace() {
            if (!st.place) return;
            st.place = null;
            updateControls();
            paint('mine');
            track('bs_place_cancel', {});
        }

        function placeHint() {
            var left = st.place.ships.length - placedCount();
            if (!left) return T.st_place_ready;
            return fill(T.st_place_left, { n: left });
        }

        function placeTap(i, via) {
            var p = st.place;
            if (!p) return;
            var k = placeShipAt(i);
            var len, list, msg;
            if (k >= 0) {
                if (p.sel !== k) {
                    p.sel = k;
                    p.dir = dirOf(p.ships[k]);
                    msg = fill(T.st_sel, { ship: shipName(E.FLEET[k]), cells: cellsText(p.ships[k]) });
                } else {
                    msg = rotate();
                }
            } else {
                var sel = p.sel >= 0 ? p.sel : nextUnplaced(0);
                if (sel < 0) {
                    msg = T.st_place_full;
                } else {
                    len = E.FLEET[sel];
                    var dir = p.ships[sel] ? dirOf(p.ships[sel]) : p.dir;
                    list = cellsFrom(i, len, dir);
                    if (!fits(sel, list)) {
                        msg = T.st_place_bad;
                        st.meta.placeBad = (st.meta.placeBad || 0) + 1;
                    } else {
                        var wasNew = !p.ships[sel];
                        p.ships[sel] = list;
                        st.meta.manual = 1;
                        msg = fill(T.st_put, { ship: shipName(len), cells: cellsText(list) });
                        p.sel = wasNew ? nextUnplaced(sel + 1) : -1;
                        if (p.sel >= 0) p.dir = dirOf(p.ships[p.sel]) || dir;
                        msg += ' ' + placeHint();
                    }
                }
            }
            paintPlace();
            say(msg);
        }

        function rotate() {
            var p = st.place;
            if (p.sel < 0 || !p.ships[p.sel]) {
                if (p.sel < 0 && nextUnplaced(0) < 0) return T.st_place_full;
                p.dir = 1 - p.dir;
                return T.st_rotate_next;
            }
            var k = p.sel, ship = p.ships[k], len = E.FLEET[k];
            if (len === 1) return fill(T.st_sel, { ship: shipName(len), cells: cellsText(ship) });
            var list = cellsFrom(ship[0], len, 1 - dirOf(ship));
            if (!fits(k, list)) return T.st_place_bad;
            p.ships[k] = list;
            p.dir = dirOf(list);
            st.meta.manual = 1;
            return fill(T.st_put, { ship: shipName(len), cells: cellsText(list) });
        }

        function paintPlace() {
            var p = st.place, list = cells.mine;
            var ghost = p.ghost || {};
            for (var i = 0; i < 100; i++) {
                var k = placeShipAt(i);
                var cls = 'bs-cell';
                if (k >= 0) cls += ' bs-c--ship' + joins(p.ships[k], i) + (k === p.sel ? ' bs-c--sel' : '');
                if (ghost.cells && ghost.cells.indexOf(i) >= 0) cls += ghost.ok ? ' bs-c--ghost' : ' bs-c--ghost-bad';
                if (list[i].className !== cls) list[i].className = cls;
                list[i].setAttribute('data-s', '.');
            }
            if (ag.mine) ag.mine.relabel();
            paintTray();
            doneBtn.disabled = placedCount() !== p.ships.length;
            afloatEls.mine.textContent = fill(T.afloat, { n: placedCount() });
            fleets.mine.textContent = '';
        }

        function paintTray() {
            var p = st.place;
            /* Лоток перестраивается только когда он правда изменился: призрак под
               мышью перерисовывает доску на каждом движении. */
            var sig = p.sel + '|' + p.ships.map(function (s) { return s ? s.join(',') : '-'; }).join(';');
            if (trayEl.__sig === sig) return;
            trayEl.__sig = sig;
            trayEl.textContent = '';
            p.ships.forEach(function (ship, k) {
                var b = doc.createElement('button');
                b.type = 'button';
                b.className = 'bs-tray-ship' + (ship ? ' is-placed' : '') + (k === p.sel ? ' is-sel' : '');
                b.setAttribute('aria-pressed', k === p.sel ? 'true' : 'false');
                b.setAttribute('aria-label', shipName(E.FLEET[k]) + (ship ? ', ' + cellsText(ship) : ', ' + T.c_in_tray));
                for (var n = 0; n < E.FLEET[k]; n++) b.appendChild(doc.createElement('i'));
                b.addEventListener('click', function () {
                    p.sel = p.sel === k ? -1 : k;
                    if (p.sel >= 0 && p.ships[k]) p.dir = dirOf(p.ships[k]);
                    paintPlace();
                    say(p.sel >= 0
                        ? fill(T.st_sel, { ship: shipName(E.FLEET[k]), cells: p.ships[k] ? cellsText(p.ships[k]) : T.c_in_tray })
                        : placeHint());
                });
                trayEl.appendChild(b);
            });
        }

        /* Призрак корабля под мышью: куда он встанет и встанет ли. */
        grids.mine.addEventListener('pointermove', function (e) {
            var p = st.place;
            if (!p || e.pointerType !== 'mouse') return;
            var c = e.target && e.target.closest ? e.target.closest('.bs-cell') : null;
            if (!c) return;
            var i = parseInt(c.getAttribute('data-i'), 10);
            var g = null;
            if (placeShipAt(i) < 0) {
                var sel = p.sel >= 0 ? p.sel : nextUnplaced(0);
                if (sel >= 0) {
                    var list = cellsFrom(i, E.FLEET[sel], p.ships[sel] ? dirOf(p.ships[sel]) : p.dir);
                    g = { cells: list, ok: fits(sel, list) };
                }
            }
            var was = p.ghost ? p.ghost.cells.join() + p.ghost.ok : '';
            var is = g ? g.cells.join() + g.ok : '';
            if (was === is) return;
            p.ghost = g;
            paintPlace();
        });
        grids.mine.addEventListener('pointerleave', function () {
            if (st.place && st.place.ghost) { st.place.ghost = null; paintPlace(); }
        });
        grids.mine.addEventListener('keydown', function (e) {
            if (!st.place || e.altKey || e.ctrlKey || e.metaKey) return;
            if (e.key === 'r' || e.key === 'R' || e.key === 'к' || e.key === 'К') {
                e.preventDefault();
                say(rotate());
                paintPlace();
            }
        });

        placeBtn.addEventListener('click', openPlace);
        q('[data-bs-place-rotate]').addEventListener('click', function () { say(rotate()); paintPlace(); });
        q('[data-bs-place-random]').addEventListener('click', function () {
            if (!st.place) return;
            st.place.ships = E.placeFleet(st.g);
            st.place.sel = -1;
            st.meta.reshuffles++;
            track('bs_reshuffle', {});
            paintPlace();
            say(T.st_shuffled + ' ' + T.st_place_ready);
        });
        q('[data-bs-place-clear]').addEventListener('click', function () {
            if (!st.place) return;
            st.place.ships = st.place.ships.map(function () { return null; });
            st.place.sel = 0;
            st.place.dir = 0;
            paintPlace();
            say(T.st_cleared);
        });
        q('[data-bs-place-cancel]').addEventListener('click', function () {
            cancelPlace();
            startStatus();
            announce(T.st_start);
        });
        doneBtn.addEventListener('click', function () {
            var p = st.place;
            if (!p || placedCount() !== p.ships.length) { say(placeHint()); return; }
            st.g.b[0].s = p.ships.map(function (s) { return s.slice(); });
            st.place = null;
            track('bs_place_done', { manual: st.meta.manual ? 1 : 0, bad: st.meta.placeBad || 0 });
            updateControls();
            paint('mine');
            persist();
            say(T.st_placed);
            if (ag.enemy) ag.enemy.focus(ag.enemy.index(), true);
        });

        /* ─── Выстрел ─────────────────────────────────────────── */

        function fireAt(i, via) {
            var g = st.g;
            if (!g || isNaN(i)) return;
            if (st.place) { say(T.st_place_first); return; }
            if (st.busy) {
                if (!st.meta.busy++) track('bs_busy_tap', { level: g.lv });
                return;
            }
            if (g.win >= 0) { announce(st.statusText); return; }
            wakeAudio();
            var m = g.b[1].m.charAt(i);
            if (m !== '.') {
                var why = m === '+' ? T.st_empty : T.st_known;
                st.meta.known++;
                if (st.meta.known <= 3) track('bs_known', { m: m === '+' ? 'empty' : 'shot' });
                say(name(i) + ': ' + why);
                return;
            }
            if (E.fresh(g)) {
                st.meta.first = via || '';
                try { if (global.__stage) global.__stage('shot', { level: g.lv, via: via || '' }); } catch (e) {}
            }
            tick();
            st.meta.input[via || 'other'] = (st.meta.input[via || 'other'] || 0) + 1;

            var before = { s: g.b[0].s, m: g.b[0].m };
            var res = E.fire(g, i);
            persist();
            var me = res.me;
            var line = myLine(i, me);
            st.last.enemy = i;
            addLog(T.you, line);
            paint('enemy');
            paintCounts();
            fx('enemy', i, me.r === 'miss' ? 'splash' : me.r === 'hit' ? 'boom' : 'sink');
            sound(me.r === 'miss' ? 'miss' : me.r === 'hit' ? 'hit' : 'sunk');
            if (me.r === 'hit') buzz(25);
            else if (me.r === 'sunk') buzz([40, 40, 60]);
            updateControls();

            if (g.win === 0) { finish(true, line); return; }
            if (me.r !== 'miss') {
                say(line + '. ' + T.st_again);
                return;
            }

            /* Ход соперника — по одному выстрелу, на копии своей доски. */
            var who = levelName(g.lv);
            var careless = g.lv === 'novice';
            st.show0 = before;
            st.busy = true;
            root.classList.add('bs-busy');
            updateControls();
            status(line + '. ' + fill(T.st_ai, { name: who }));
            var token = ++st.token;
            var parts = [];
            var k = 0;
            function step() {
                if (token !== st.token) return;
                if (k >= res.ai.length) {
                    st.show0 = null;
                    st.busy = false;
                    root.classList.remove('bs-busy');
                    paint('mine');
                    updateControls();
                    var text = line + '. ' + who + ': ' + parts.join('; ');
                    if (st.g.win === 1) { finish(false, text); return; }
                    say(text + '. ' + T.st_turn);
                    return;
                }
                var e = res.ai[k++];
                E.shoot(st.show0, e.cell, careless);
                st.last.mine = e.cell;
                paint('mine');
                fx('mine', e.cell, e.r === 'miss' ? 'splash' : e.r === 'hit' ? 'boom' : 'sink');
                sound(e.r === 'miss' ? 'miss' : e.r === 'hit' ? 'hit' : 'sunk');
                if (e.r !== 'miss') buzz(e.r === 'hit' ? 60 : [80, 50, 80]);
                var t = aiLine(e, st.g.b[0]);
                parts.push(t);
                addLog(who, t);
                setTimeout(step, AI_STEP_MS);
            }
            setTimeout(step, AI_STEP_MS);
        }

        function finishStatus() {
            var g = st.g;
            var won = g.win === 0;
            var shots = shotsAt(g.b[1]);
            var acc = shots ? Math.round(hitsAt(g.b[1]) * 100 / shots) : 0;
            var msg = won
                ? fill(T.st_win, { n: shots }) + ' ' + fill(T.accuracy, { p: acc })
                : fill(T.st_lose, { name: levelName(g.lv) });
            return msg;
        }

        function finish(won, text) {
            var g = st.g;
            st.busy = false;
            root.classList.remove('bs-busy');
            var shots = shotsAt(g.b[1]);
            var hits = hitsAt(g.b[1]);
            var rec = stats[g.lv] || [0, 0];
            rec[won ? 0 : 1]++;
            var record = won && (!rec[2] || shots < rec[2]);
            if (record) rec[2] = shots;
            stats[g.lv] = rec;
            save('stats', stats);
            save('game', null);
            if (!won) st.revealed = true;
            paint('enemy');
            paint('mine');
            paintCounts();
            updateControls();
            var msg = finishStatus() + (record && rec[0] > 1 ? ' ' + T.st_record : '');
            var full = trimDot(text) + '. ' + msg;
            st.over = { text: full, kind: won ? 'win' : 'lose' };
            status(full, st.over.kind, playAgain());
            announce(full, true);
            setTimeout(function () { sound(won ? 'win' : 'lose'); }, 900);
            buzz(won ? [60, 60, 60, 60, 120] : 300);
            var m = st.meta || freshMeta();
            try {
                if (global.__aeToolComplete) {
                    global.__aeToolComplete({
                        t: 'bs_game_end',
                        result: won ? 'win' : 'loss',
                        level: g.lv,
                        shots: shots,
                        hits: hits,
                        acc: shots ? Math.round(hits * 100 / shots) : 0,
                        ai_shots: shotsAt(g.b[0]),
                        left: won ? E.afloat(g.b[0]).length : E.afloat(g.b[1]).length,
                        dur_s: durS(),
                        input: mainInput(),
                        first: m.first || '',
                        placed: m.manual ? 'manual' : (m.reshuffles ? 'random' : 'auto'),
                        restored: m.restored ? 1 : 0,
                        known: m.known,
                        sound: soundOn ? 1 : 0,
                        record: record ? 1 : 0
                    });
                }
            } catch (e) {}
        }

        /* ─── Звук и вибрация ─────────────────────────────────── */

        var actx = null, out = null, bufs = {}, pending = null;

        /* Нет настоящего Web Audio — играем молча: заглушка timbrica-core.js
           показывает тост на каждый `new AudioContext()`, а игре звук не обязателен.
           Флаг выставляет сама заглушка; по конструктору её не узнать — его
           оборачивает Proxy пула контекстов. */
        function nativeAC() {
            if (global.__webAudioMissing) return null;
            return global.AudioContext || global.webkitAudioContext || null;
        }
        var AC = nativeAC();
        if (!AC) {
            soundBtn.disabled = true;
            soundBtn.title = T.no_sound || '';
        }

        function makeCtx() {
            if (!AC) return null;
            try { return new AC({ latencyHint: 'interactive' }); } catch (e) { AC = null; throw e; }
        }

        function adopt(c) {
            if (c && c !== actx) {
                actx = c;
                out = null;
                bufs = {};
                pending = null;
            }
            return actx;
        }

        /* Только из нажатия: браузер включает звук в момент жеста. */
        function wakeAudio() {
            if (!soundOn || !AC) return;
            try {
                if (typeof global.__liveAudioCtxReady === 'function') {
                    global.__liveAudioCtxReady(actx, makeCtx).then(function (c) { adopt(c); loadSounds(); }, function () {});
                } else {
                    adopt(actx || makeCtx());
                    if (actx && actx.state === 'suspended' && actx.resume) actx.resume();
                    loadSounds();
                }
            } catch (e) {}
        }

        function decode(buf) {
            return new Promise(function (resolve, reject) {
                var p = actx.decodeAudioData(buf, resolve, reject);
                if (p && typeof p.then === 'function') p.then(resolve, reject);
            });
        }

        function loadSounds() {
            if (!actx || pending) return pending;
            var ctx = actx;
            pending = Promise.all(Object.keys(SND).map(function (key) {
                return fetch(SND[key]).then(function (r) { return r.arrayBuffer(); }).then(decode).then(function (b) {
                    if (ctx === actx) bufs[key] = b;
                }, function () {});
            }));
            return pending;
        }

        function sound(key) {
            if (!soundOn || !actx) return;
            var asked = Date.now();
            function go() {
                var b = bufs[key];
                if (!b || !actx || Date.now() - asked > 1500) return;
                try {
                    if (!out) {
                        out = actx.createGain();
                        out.gain.value = 0.8;
                        out.connect(actx.destination);
                    }
                    var src = actx.createBufferSource();
                    src.buffer = b;
                    src.connect(out);
                    src.start();
                } catch (e) {}
            }
            if (bufs[key]) go();
            else if (pending) pending.then(go);
        }

        /* Отдача в руку — там, где браузер её умеет (Android); iPhone молча пропускает.
           Выключатель тот же, что у звука: «эффекты» включает и выключает человек. */
        function buzz(pattern) {
            if (!soundOn) return;
            try { if (navigator.vibrate) navigator.vibrate(pattern); } catch (e) {}
        }

        root.addEventListener('pointerdown', wakeAudio, { passive: true });
        root.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') wakeAudio(); });

        /* ─── Старт: незаконченная партия или новая ───────────── */

        var saved = load('game', null);
        if (saved && valid(saved.g)) {
            st.g = saved.g;
            st.log = Array.isArray(saved.log) ? saved.log : [];
            st.meta = saved.meta && typeof saved.meta === 'object' ? saved.meta : freshMeta();
            st.meta.input = st.meta.input || {};
            st.meta.at = now();
            st.meta.restored = 1;
            setLevelRadio(st.g.lv);
            paintAll();
            status(T.st_restored + ' ' + (E.fresh(st.g) ? T.st_start : T.st_turn));
            track('bs_restore', { level: st.g.lv, shots: shotsAt(st.g.b[1]) });
            try { if (global.__stage) global.__stage('ready', { level: st.g.lv, restored: 1 }); } catch (e) {}
        } else {
            var lv = load('level', 'captain');
            newGame(E.LEVELS.indexOf(lv) >= 0 ? lv : 'captain');
        }

        global.__battleship = {
            state: function () { return st; },
            fire: fireAt,
            newGame: newGame,
            place: { open: openPlace, tap: placeTap, rotate: function () { return rotate(); } }
        };
    }

    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
    else boot();
})(window, document);
